import React from 'react';
import { Avatar, Box, Divider, List, ListItem, ListItemAvatar, ListItemText, Paper, Typography } from '@mui/material';
import { MessagingToDo } from '@uplift/api';
import { ScrollableView } from '../../Layout';
import { AsyncButton, Button } from '../../Button';
import { asyncWait } from '@uplift/utils';
import { useArchiveMessagingToDoMutation, useCompleteMessagingToDoMutation, useUserDisplayNameQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';
import { MessageItem } from '../Messages/MessageBubble.component';
import { ArrowForward } from '@mui/icons-material';
import { useMessagingContext } from '../MessagingProvider.component';


export const ToDoContent: React.FC<{ todo: MessagingToDo }> = ({ todo }) => {
  const me = useCurrentuser();
  const messagingContext = useMessagingContext();
  const archiveMessagingToDoMutation = useArchiveMessagingToDoMutation();
  const completeMessagingToDoMutation = useCompleteMessagingToDoMutation();
  const { data: participant } = useUserDisplayNameQuery(todo.participantId);

  const completeCheckboxLabel = React.useMemo(() => {
    if (todo.completedAt) {
      return 'Mark as Incomplete';
    }
    return 'Mark as Completed';
  }, [ todo.completedAt ]);

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <List>
        <ListItem>
          <ListItemAvatar>
            <Avatar>{participant?.defaultDisplay[0] ?? '?'}</Avatar>
          </ListItemAvatar>
          <ListItemText primary={participant?.defaultDisplay} />
        </ListItem>
      </List>
      <Box p={2} pt={0}>
        <Box
          component={Paper}
          variant="outlined"
          sx={{
            p: 2,
            backgroundColor: 'secondary.background',
            borderColor: 'secondary.border',
          }}
        >
          <Typography variant="subtitle2" color="secondary" sx={{ mb: 1 }}>Description</Typography>
          {todo.description}
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" px={2} pt={1}>
        <Typography variant="body1" color="text.secondary">
          Related Message
        </Typography>
        <Button
          sx={{ textTransform: 'none' }}
          endIcon={<ArrowForward />}
          color="secondary"
          onClick={() => {
            messagingContext.openModal(todo.message.conversationId, todo.message.id);
          }}
        >View Conversation</Button>
      </Box>
      <ScrollableView width="100%" flex={1}>
        <Box p={2} pb={1} pt={1}>
          <MessageItem
            color="secondary"
            message={todo.message}
            direction={me.id === todo.message.senderId ? 'send' : 'receive'}
            downloadableAttachments
          />
        </Box>
      </ScrollableView>
      <Divider />
      <Box display="flex" gap={1} p={2} alignItems="center">
        <AsyncButton
          // color="error"
          variant="outlined"
          disabled={!!todo.completedAt}
          onClick={async () => {
            await Promise.all([
              archiveMessagingToDoMutation.mutateAsync({ todoId: todo.id, archive: !todo.archivedAt }),
              asyncWait(600),
            ]);
          }}
        >
          {todo.archivedAt ? 'Unarchive' : 'Archive'}
        </AsyncButton>
        <Box flex={1} />
        <AsyncButton
          color="secondary"
          variant="contained"
          disabled={completeMessagingToDoMutation.isLoading || !!todo.archivedAt}
          onClick={async () => {
            await Promise.all([
              completeMessagingToDoMutation.mutateAsync({ todoId: todo.id, complete: !todo.completedAt }),
              asyncWait(600),
            ]);
          }}
        >
          {completeCheckboxLabel}
        </AsyncButton>
      </Box>
    </Box>
  );
};
